/**
 * Color Variant Switcher
 * Dynamically switches between consolidated products based on color selection
 */

(function() {
  'use strict'; 

  class ColorVariantSwitcher {
    constructor() {
      this.container = document.querySelector('.product-color-variants');
      this.productContainer = document.querySelector('[data-section-type="product"]');
      this.sizeSelector = document.querySelector('.color-variant-size-selector');
      this.sizeDropdown = document.querySelector('.size-selector-dropdown');
      this.isLoading = false;
      this.productCache = new Map();
      this.colorVariantsData = {};
      this.currentColor = '';
      this.currentSize = '';
      this.currentHandle = '';
      this.relatedProductHandles = [];
      
      if (this.container && this.productContainer) {
        this.init();
      }
    }

    async init() {
      this.parseInitialData();
      this.bindEvents();
      this.cacheCurrentProduct();
      
      if (this.isConsolidationMode) {
        // Consolidation mode: color variants data is already available
        this.createColorOptions();
        // No size selector needed in consolidation mode
      } else {
        // Multi-product mode: fetch and build color variants from related products
        await this.buildColorVariantsFromProducts();
        this.createColorOptions();
        this.updateSizeSelector();
      }
    }

    parseInitialData() {
      try {
        // Check if this is consolidation mode or multi-product mode
        this.isConsolidationMode = this.container.dataset.consolidationMode === 'true';
        
        if (this.isConsolidationMode) {
          // Consolidation mode: color variants data is already built in Liquid
          const colorVariantsJson = this.container.dataset.colorVariants;
          this.colorVariantsData = JSON.parse(colorVariantsJson);
        } else {
          // Multi-product mode: need to fetch related products
          const relatedProductsStr = this.container.dataset.relatedProducts;
          this.relatedProductHandles = relatedProductsStr ? relatedProductsStr.split(',') : [];
        }
        
        this.currentColor = this.container.dataset.currentColor;
        this.currentSize = this.container.dataset.currentSize;
        this.currentHandle = this.container.dataset.currentHandle;
      } catch (error) {
        console.error('Error parsing initial data:', error);
        this.relatedProductHandles = [];
        this.colorVariantsData = {};
      }
    }

    async buildColorVariantsFromProducts() {
      if (this.relatedProductHandles.length === 0) {
        this.hideColorSwitcher();
        return;
      }

      this.setLoadingState(true);
      this.colorVariantsData = {};

      try {
        // Fetch all related products
        const productPromises = this.relatedProductHandles.map(handle => 
          this.fetchProductData(handle.trim())
        );
        
        const products = await Promise.all(productPromises);
        
        // Group products by color
        products.forEach(productData => {
          if (!productData || !productData.metafields) return;
          
          const color = productData.metafields.current_color || this.extractColorFromTitle(productData.title);
          const size = productData.metafields.current_size || this.extractSizeFromTitle(productData.title);
          
          if (!this.colorVariantsData[color]) {
            this.colorVariantsData[color] = [];
          }
          
          this.colorVariantsData[color].push({
            handle: productData.handle,
            size: size,
            title: size || 'Standard',
            color: color
          });
        });

        // Sort products within each color by size
        Object.keys(this.colorVariantsData).forEach(color => {
          this.colorVariantsData[color].sort((a, b) => {
            const sizeA = parseInt(a.size) || 0;
            const sizeB = parseInt(b.size) || 0;
            return sizeA - sizeB;
          });
        });

      } catch (error) {
        console.error('Error building color variants from products:', error);
        this.showError('Failed to load color options');
      } finally {
        this.setLoadingState(false);
      }
    }

    extractColorFromTitle(title) {
      const colorKeywords = {
        'white': 'Matte White',
        'black': 'Matte Black', 
        'nickel': 'Brushed Nickel',
        'bronze': 'Oil Rubbed Bronze',
        'brass': 'Antique Brass'
      };
      
      const lowerTitle = title.toLowerCase();
      for (const [keyword, fullColor] of Object.entries(colorKeywords)) {
        if (lowerTitle.includes(keyword)) {
          return fullColor;
        }
      }
      
      return 'Standard';
    }

    extractSizeFromTitle(title) {
      const sizeMatch = title.match(/(\d+)\s*(?:inch|")/i);
      return sizeMatch ? `${sizeMatch[1]}"` : '';
    }

    hideColorSwitcher() {
      if (this.container) {
        this.container.style.display = 'none';
      }
    }

    createColorOptions() {
      const optionsContainer = this.container.querySelector('.color-variants-options');
      if (!optionsContainer) return;

      // Clear loading text
      optionsContainer.innerHTML = '';

      // If no color variants found, hide the switcher
      if (Object.keys(this.colorVariantsData).length <= 1) {
        this.hideColorSwitcher();
        return;
      }

      Object.entries(this.colorVariantsData).forEach(([colorName, colorData]) => {
        const isActive = colorName === this.currentColor;
        
        const colorOption = document.createElement('div');
        colorOption.className = `color-variant-option ${isActive ? 'active' : ''}`;
        colorOption.setAttribute('data-color', colorName);
        
        if (this.isConsolidationMode) {
          // In consolidation mode, store the color data directly
          colorOption.setAttribute('data-color-data', JSON.stringify(colorData));
        } else {
          // In multi-product mode, store the product array
          colorOption.setAttribute('data-products', JSON.stringify(colorData));
        }
        
        const colorSwatch = document.createElement('div');
        colorSwatch.className = `color-variant-swatch color-swatch-${colorName.toLowerCase().replace(/\s+/g, '-')}`;
        colorSwatch.title = colorName;
        
        const colorNameSpan = document.createElement('span');
        colorNameSpan.className = 'color-variant-name';
        colorNameSpan.textContent = colorName;
        
        colorSwatch.appendChild(colorNameSpan);
        colorOption.appendChild(colorSwatch);
        optionsContainer.appendChild(colorOption);
      });
    }

    updateSizeSelector() {
      if (!this.sizeSelector || !this.sizeDropdown) return;

      const currentColorProducts = this.colorVariantsData[this.currentColor] || [];
      
      if (currentColorProducts.length > 1) {
        // Show size selector
        this.sizeSelector.style.display = 'block';
        
        // Clear existing options
        this.sizeDropdown.innerHTML = '<option value="">Select size...</option>';
        
        // Add size options
        currentColorProducts.forEach(product => {
          const option = document.createElement('option');
          option.value = product.handle;
          option.textContent = product.title || product.size;
          option.setAttribute('data-size', product.size || '');
          
          // Mark current product as selected
          if (product.handle === window.location.pathname.split('/').pop()) {
            option.selected = true;
          }
          
          this.sizeDropdown.appendChild(option);
        });
      } else {
        // Hide size selector
        this.sizeSelector.style.display = 'none';
      }
    }

    bindEvents() {
      // Handle color variant clicks
      this.container.addEventListener('click', (e) => {
        const colorOption = e.target.closest('.color-variant-option');
        if (colorOption && !colorOption.classList.contains('active')) {
          this.switchToColor(colorOption);
        }
      });

      // Handle size selection
      if (this.sizeDropdown) {
        this.sizeDropdown.addEventListener('change', (e) => {
          const selectedHandle = e.target.value;
          if (selectedHandle) {
            this.switchToProductByHandle(selectedHandle);
          }
        });
      }
    }

    cacheCurrentProduct() {
      // Cache current product data for quick switching
      const currentHandle = window.location.pathname.split('/').pop();
      
      this.productCache.set(currentHandle, {
        handle: currentHandle,
        title: document.querySelector('.product-title h1')?.textContent || '',
        price: document.querySelector('[data-product-price]')?.textContent || '',
        comparePrice: document.querySelector('[data-compare-price]')?.textContent || '',
        images: this.getCurrentImages(),
        formAction: document.querySelector('[data-product-form]')?.action || '',
        variantId: document.querySelector('[name="id"]')?.value || '',
        availability: document.querySelector('.product-availability')?.textContent || ''
      });
    }

    getCurrentImages() {
      const images = [];
      document.querySelectorAll('.product-detail__images img').forEach(img => {
        images.push({
          src: img.src,
          alt: img.alt,
          dataSrc: img.dataset.src || ''
        });
      });
      return images;
    }

    async switchToColor(colorOption) {
      if (this.isLoading) return;
      
      const color = colorOption.dataset.color;
      
      if (this.isConsolidationMode) {
        // Consolidation mode: use pre-built color data
        const colorData = this.colorVariantsData[color];
        if (colorData) {
          this.switchToColorData(colorData, colorOption);
        }
      } else {
        // Multi-product mode: original logic
        const productsData = JSON.parse(colorOption.dataset.products);
        
        // If only one product for this color, switch directly
        if (productsData.length === 1) {
          this.switchToProductByHandle(productsData[0].handle, colorOption);
        } else {
          // Multiple products, update color and show size selector
          this.updateActiveColor(colorOption);
          this.currentColor = color;
          this.updateSizeSelector();
          
          // Switch to first product in the array by default
          this.switchToProductByHandle(productsData[0].handle);
        }
      }
    }

    async switchToColorData(colorData, colorOption) {
      this.setLoadingState(true);
      
      try {
        // If we have a handle, fetch full product data to get all images
        if (colorData.handle) {
          let productData = this.productCache.get(colorData.handle);
          
          if (!productData) {
            // Fetch full product data including media
            productData = await this.fetchProductData(colorData.handle);
            this.productCache.set(colorData.handle, productData);
          }
          
          // Update with full product data (which includes all images)
          this.updateProductData(productData);
        } else {
          // Fallback to basic update if no handle
          this.updateProductDataFromColorData(colorData);
        }
        
        this.updateActiveColor(colorOption);
        
        // Don't change URL in consolidation mode
        
      } catch (error) {
        console.error('Error switching color:', error);
        this.showError('Unable to load selected color option. Please try again.');
      } finally {
        this.setLoadingState(false);
      }
    }

    updateProductDataFromColorData(colorData) {
      // Update price
      const priceElement = document.querySelector('[data-product-price]');
      if (priceElement && colorData.price) {
        priceElement.innerHTML = `<span class="theme-money large-title">${this.formatPrice(colorData.price)}</span>`;
        
        // Add or remove product-price__reduced class based on compare price
        if (colorData.compare_price && colorData.compare_price > colorData.price) {
          priceElement.classList.add('product-price__reduced');
        } else {
          priceElement.classList.remove('product-price__reduced');
        }
      }

      // Update compare price
      const comparePriceElement = document.querySelector('[data-compare-price]');
      if (comparePriceElement && colorData.compare_price && colorData.compare_price > colorData.price) {
        comparePriceElement.innerHTML = `<span class="product-price__compare theme-money">${this.formatPrice(colorData.compare_price)}</span>`;
        comparePriceElement.style.display = '';
      } else if (comparePriceElement) {
        comparePriceElement.style.display = 'none';
      }

      // Update all variant ID inputs
      const variantInputs = document.querySelectorAll('[name="id"], [data-product-secondary-select]');
      variantInputs.forEach(input => {
        if (input && colorData.variant_id) {
          input.value = colorData.variant_id;
        }
      });

      // Update availability
      const availabilityElement = document.querySelector('.product-availability');
      if (availabilityElement) {
        const availability = colorData.available ? 'In Stock' : 'Out of Stock';
        availabilityElement.textContent = availability;
        availabilityElement.className = `product-availability ${availability.toLowerCase().replace(' ', '-')}`;
      }

      // Update featured image
      if (colorData.featured_image) {
        this.updateFeaturedImage(colorData.featured_image);
      }

      // Trigger custom event for other scripts
      this.productContainer.dispatchEvent(new CustomEvent('colorVariantChanged', {
        detail: { colorData, handle: colorData.handle }
      }));
    }

    async switchToProductByHandle(handle, colorOption = null) {
      if (this.isLoading) return;
      
      this.setLoadingState(true);
      
      try {
        // Check cache first
        let productData = this.productCache.get(handle);
        
        if (!productData) {
          // Fetch product data
          productData = await this.fetchProductData(handle);
          this.productCache.set(handle, productData);
        }
        
        // Update UI
        this.updateProductData(productData);
        if (colorOption) {
          this.updateActiveColor(colorOption);
        }
        this.updateURL(handle);
        
        // Update size selector to reflect current product
        if (this.sizeDropdown) {
          this.sizeDropdown.value = handle;
        }
        
      } catch (error) {
        console.error('Error switching color variant:', error);
        this.showError('Unable to load selected product. Please try again.');
      } finally {
        this.setLoadingState(false);
      }
    }

    async fetchProductData(handle) {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        throw new Error(`Failed to fetch product: ${response.status}`);
      }
      
      const product = await response.json();
      
      // Fetch metafields and full page content by scraping the product page
      let metafields = {};
      let pageContent = null;
      try {
        const pageResponse = await fetch(`/products/${handle}`);
        if (pageResponse.ok) {
          const pageText = await pageResponse.text();
          pageContent = pageText; // Store full page content
          const parser = new DOMParser();
          const doc = parser.parseFromString(pageText, 'text/html');
          
          // Extract metafields from data attributes or script tags
          const productScript = doc.querySelector('script[data-product-json]') || 
                              Array.from(doc.querySelectorAll('script')).find(script => 
                                script.textContent && script.textContent.includes('product') && 
                                script.textContent.includes('{')
                              );
          if (productScript) {
            const scriptContent = productScript.textContent;
            // Try to extract metafields from various possible formats
            metafields = this.extractMetafieldsFromPage(scriptContent, pageText);
          }
        }
      } catch (error) {
        console.warn('Could not fetch metafields for', handle, error);
      }
      
      // Get featured image URL
      let featuredImageUrl = '';
      if (product.featured_media && product.featured_media.preview_image) {
        featuredImageUrl = product.featured_media.preview_image.src;
      } else if (product.images && product.images.length > 0) {
        featuredImageUrl = product.images[0];
      }

      const result = {
        handle: product.handle,
        title: product.title,
        price: this.formatPrice(product.price),
        comparePrice: product.compare_at_price ? this.formatPrice(product.compare_at_price) : '',
        formAction: `/cart/add`,
        variantId: product.variants[0]?.id || '',
        availability: product.available ? 'In Stock' : 'Out of Stock',
        images: product.images || [],
        media: product.media || [], // Include all media for slider
        featuredImage: featuredImageUrl,
        description: product.description || '',
        metafields: metafields,
        pageContent: pageContent // Include full page content for image gallery extraction
      };
      
      console.log('Fetched product data:', {
        handle: result.handle,
        mediaCount: result.media.length,
        imagesCount: result.images.length,
        hasPageContent: !!result.pageContent
      });
      
      return result;
    }

    extractMetafieldsFromPage(scriptContent, pageContent) {
      const metafields = {};
      
      // Try to extract current_color and current_size from the page content
      // Look for data attributes that might contain metafield values
      const colorMatch = pageContent.match(/data-current-color=["']([^"']+)["']/i);
      if (colorMatch) {
        metafields.current_color = colorMatch[1];
      }
      
      const sizeMatch = pageContent.match(/data-current-size=["']([^"']+)["']/i);
      if (sizeMatch) {
        metafields.current_size = sizeMatch[1];
      }
      
      return metafields;
    }

    updateProductData(productData) {
      // Update title
      const titleElement = document.querySelector('.product-title h1');
      if (titleElement) {
        titleElement.textContent = productData.title;
      }

      // Update price
      const priceElement = document.querySelector('[data-product-price]');
      if (priceElement) {
        priceElement.innerHTML = `<span class="theme-money large-title">${productData.price}</span>`;
        
        // Add or remove product-price__reduced class based on compare price
        if (productData.comparePrice) {
          priceElement.classList.add('product-price__reduced');
        } else {
          priceElement.classList.remove('product-price__reduced');
        }
      }

      // Update compare price
      const comparePriceElement = document.querySelector('[data-compare-price]');
      if (comparePriceElement && productData.comparePrice) {
        comparePriceElement.innerHTML = `<span class="product-price__compare theme-money">${productData.comparePrice}</span>`;
        comparePriceElement.style.display = '';
      } else if (comparePriceElement) {
        comparePriceElement.style.display = 'none';
      }

      // Update form action and variant ID  
      const form = document.querySelector('[data-product-form]');
      if (form) {
        form.action = productData.formAction;
      }

      // Update all variant ID inputs (there may be multiple)
      const variantInputs = document.querySelectorAll('[name="id"], [data-product-secondary-select]');
      variantInputs.forEach(input => {
        if (input) {
          input.value = productData.variantId;
        }
      });

      // Update availability
      const availabilityElement = document.querySelector('.product-availability');
      if (availabilityElement) {
        availabilityElement.textContent = productData.availability;
        availabilityElement.className = `product-availability ${productData.availability.toLowerCase().replace(' ', '-')}`;
      }

      // Update images - prioritize media/images for slick slider functionality
      console.log('Product data check:', {
        hasPageContent: !!productData.pageContent,
        mediaLength: productData.media ? productData.media.length : 0,
        imagesLength: productData.images ? productData.images.length : 0,
        hasFeaturedImage: !!productData.featuredImage
      });
      
      if (productData.media && productData.media.length > 0) {
        // Use new slider functionality when we have media data
        console.log(`Updating gallery with ${productData.media.length} media items`);
        this.updateProductGalleryWithSlider(productData);
      } else if (productData.images && productData.images.length > 0) {
        // Use new slider functionality when we have images array
        console.log(`Updating gallery with ${productData.images.length} images`);
        this.updateProductGalleryWithSlider(productData);
      } else if (productData.pageContent) {
        // Fallback to page content parsing if no media/images data
        this.updateProductImages(productData.pageContent);
      } else if (productData.featuredImage) {
        // Final fallback to single image update
        console.log('Using featured image fallback');
        this.updateFeaturedImage(productData.featuredImage);
      }

      // Trigger custom event for other scripts
      this.productContainer.dispatchEvent(new CustomEvent('colorVariantChanged', {
        detail: { productData, handle: productData.handle }
      }));
    }

    updateProductImages(pageContent) {
      // Extract images from the page content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = pageContent;
      
      const newImagesContainer = tempDiv.querySelector('.product-detail__images');
      const currentImagesContainer = document.querySelector('.product-detail__images');
      
      if (newImagesContainer && currentImagesContainer) {
        // Replace images container
        currentImagesContainer.innerHTML = newImagesContainer.innerHTML;
        
        // Fix images that use background-image instead of src
        const images = currentImagesContainer.querySelectorAll('img');
        
        images.forEach((img) => {
          // Try to get image URL from various sources
          let imageUrl = null;
          
          // Check background-image style first
          if (img.style.backgroundImage && img.style.backgroundImage.includes('url(')) {
            const urlMatch = img.style.backgroundImage.match(/url\((.*?)\)/);
            if (urlMatch && urlMatch[1]) {
              imageUrl = urlMatch[1].replace(/["']/g, '');
            }
          }
          
          // Check data-src attribute
          if (!imageUrl && img.dataset.src) {
            imageUrl = img.dataset.src;
          }
          
          // Check existing src (but not if it's blank or the current page URL)
          if (!imageUrl && img.src && img.src !== 'about:blank' && img.src !== window.location.href) {
            imageUrl = img.src;
          }
          
          // Set the src if we found a URL
          if (imageUrl) {
            // Clear background-image first
            img.style.backgroundImage = '';
            
            // Create a promise to ensure image loads
            const imageLoadPromise = new Promise((resolve) => {
              if (img.complete && img.naturalHeight !== 0) {
                resolve();
              } else {
                img.onload = resolve;
                img.onerror = resolve; // Resolve even on error to prevent hanging
              }
            });
            
            // Set the src
            img.src = imageUrl;
            
            // Wait for image to load before making it visible
            imageLoadPromise.then(() => {
              img.style.opacity = '1';
              img.classList.remove('lazyload');
              img.classList.add('lazyloaded');
            });
          }
        });
        
        // Reinitialize any image gallery functionality
        if (window.initProductImages) {
          window.initProductImages();
        }
        
        // Trigger lazyload update if available
        if (window.lazySizes) {
          window.lazySizes.autoSizer.checkElems();
        }
      }
    }

    updateFeaturedImage(featuredImageUrl) {
      // Update the main product image
      const mainImages = document.querySelectorAll('.product-detail__images img');
      const firstImage = mainImages[0];
      
      if (firstImage && featuredImageUrl) {
        // Store original src for potential rollback
        if (!firstImage.dataset.originalSrc) {
          firstImage.dataset.originalSrc = firstImage.src;
        }
        
        // Update the first (featured) image
        firstImage.src = featuredImageUrl;
        firstImage.srcset = ''; // Clear srcset to ensure our src is used
        
        // Update any associated links
        const imageLink = firstImage.closest('a');
        if (imageLink) {
          imageLink.href = featuredImageUrl;
        }
      }

      // Update first thumbnail if it exists
      const thumbnails = document.querySelectorAll('.product-detail__thumbnail img');
      const firstThumbnail = thumbnails[0];
      
      if (firstThumbnail && featuredImageUrl) {
        // Store original src for potential rollback
        if (!firstThumbnail.dataset.originalSrc) {
          firstThumbnail.dataset.originalSrc = firstThumbnail.src;
        }
        
        // Update thumbnail with a smaller version
        const thumbnailUrl = this.getSizedImageUrl(featuredImageUrl, '150x150');
        firstThumbnail.src = thumbnailUrl;
        firstThumbnail.srcset = ''; // Clear srcset
        
        // Update thumbnail link
        const thumbnailLink = firstThumbnail.closest('a');
        if (thumbnailLink) {
          thumbnailLink.href = featuredImageUrl;
        }
      }

      // Trigger image load events for any galleries or zoom functionality
      if (firstImage) {
        const event = new Event('load');
        firstImage.dispatchEvent(event);
      }
    }

    getSizedImageUrl(url, size) {
      if (!url) return url;
      
      // If the URL already has a size parameter, replace it
      if (url.includes('_') && url.includes('.')) {
        return url.replace(/(_\d+x\d*|_[a-z]+)(\.[^.]+)$/, `_${size}$2`);
      }
      
      // If no size parameter, add one before the file extension
      return url.replace(/(\.[^.]+)$/, `_${size}$1`);
    }

    updateProductGalleryWithSlider(productData) {
      const imagesContainer = document.querySelector('.product-detail__images');
      let thumbnailsContainer = document.querySelector('.product-detail__thumbnails');
      
      if (!imagesContainer) {
        console.error('Product images container not found');
        return;
      }
      
      // Create thumbnails container if it doesn't exist
      if (!thumbnailsContainer && ((productData.media && productData.media.length > 1) || (productData.images && productData.images.length > 1))) {
        thumbnailsContainer = document.createElement('div');
        thumbnailsContainer.className = 'product-detail__thumbnails';
        
        // Find the parent container and insert thumbnails after images
        const parentContainer = imagesContainer.closest('.product-detail__images-container');
        if (parentContainer) {
          parentContainer.appendChild(thumbnailsContainer);
        } else {
          imagesContainer.parentNode.appendChild(thumbnailsContainer);
        }
      }
      
      // Check if slick is already initialized and destroy it properly
      if ($(imagesContainer).hasClass('slick-initialized')) {
        $(imagesContainer).slick('unslick');
        // Remove any slick-generated elements
        $(imagesContainer).find('.slick-cloned').remove();
        $(imagesContainer).removeClass('slick-initialized slick-slider slick-dotted');
      }
      if (thumbnailsContainer && $(thumbnailsContainer).hasClass('slick-initialized')) {
        $(thumbnailsContainer).slick('unslick');
        $(thumbnailsContainer).find('.slick-cloned').remove();
        $(thumbnailsContainer).removeClass('slick-initialized slick-slider');
      }
      
      // Build new HTML for all media
      let mediaHTML = '';
      let thumbnailHTML = '';
      
      if (productData.media && productData.media.length > 0) {
        productData.media.forEach((media, index) => {
          // Skip swatch images
          if (media.src && media.src.includes('swatch-')) return;
          if (media.preview_image && media.preview_image.src && media.preview_image.src.includes('swatch-')) return;
          
          const mediaId = media.id || `media-${index}`;
          const isActive = index === 0 ? 'thumb-active' : '';
          
          if (media.media_type === 'image') {
            const imageUrl = media.src || (media.preview_image ? media.preview_image.src : '');
            if (imageUrl) {
              // Main image
              mediaHTML += `
                <div class="product-detail__image">
                  <a href="${imageUrl}" 
                     data-product-image
                     data-product-media
                     data-media-id="${mediaId}"
                     class="global-border-radius">
                    <img src="${this.getSizedImageUrl(imageUrl, '1024x1024')}" 
                         alt="${productData.title}"
                         class="responsive-image__image"
                         loading="eager">
                  </a>
                </div>
              `;
              
              // Thumbnail
              thumbnailHTML += `
                <a href="${imageUrl}"
                   class="product-detail__thumbnail global-border-radius-medium media-thumbnail media-thumbnail--media-image ${isActive}"
                   data-media-id="${mediaId}"
                   data-product-media-thumbnail>
                  <img src="${this.getSizedImageUrl(imageUrl, '300x300')}" 
                       alt="${productData.title}"
                       loading="lazy">
                </a>
              `;
            }
          }
          // You can add support for video and other media types here
        });
      } else if (productData.images && productData.images.length > 0) {
        // Fallback to images array if media is not available
        productData.images.forEach((imageUrl, index) => {
          if (imageUrl.includes('swatch-')) return;
          
          const mediaId = `image-${index}`;
          const isActive = index === 0 ? 'thumb-active' : '';
          
          mediaHTML += `
            <div class="product-detail__image">
              <a href="${imageUrl}" 
                 data-product-image
                 data-product-media
                 data-media-id="${mediaId}"
                 class="global-border-radius">
                <img src="${this.getSizedImageUrl(imageUrl, '1024x1024')}" 
                     alt="${productData.title}"
                     class="responsive-image__image"
                     loading="eager">
              </a>
            </div>
          `;
          
          thumbnailHTML += `
            <a href="${imageUrl}"
               class="product-detail__thumbnail global-border-radius-medium media-thumbnail media-thumbnail--media-image ${isActive}"
               data-media-id="${mediaId}"
               data-product-media-thumbnail>
              <img src="${this.getSizedImageUrl(imageUrl, '300x300')}" 
                   alt="${productData.title}"
                   loading="lazy">
            </a>
          `;
        });
      }
      
      // Update containers
      imagesContainer.innerHTML = mediaHTML;
      if (thumbnailsContainer && thumbnailHTML) {
        thumbnailsContainer.innerHTML = thumbnailHTML;
      }
      
      // Add loading class and temporary opacity
      imagesContainer.classList.add('loading-images');
      imagesContainer.style.opacity = '0.5';
      imagesContainer.style.transition = 'opacity 0.3s ease';
      
      // Check if we need slick (multiple images)
      const needsSlick = (productData.media && productData.media.length > 1) || (productData.images && productData.images.length > 1);
      
      if (needsSlick) {
        console.log(`Found ${productData.media?.length || productData.images?.length} images, setting up slick slider`);
        
      // Preload images before initializing slick
      const imageElements = imagesContainer.querySelectorAll('img');
      const imagePromises = Array.from(imageElements).map(img => {
        return new Promise((resolve) => {
          if (img.complete && img.naturalHeight !== 0) {
            resolve();
          } else {
            img.onload = resolve;
            img.onerror = resolve; // Resolve even on error
          }
        });
      });
      
      // Wait for all images to load, then initialize slick
      Promise.all(imagePromises).then(() => {
        // Remove loading class and restore opacity
        imagesContainer.classList.remove('loading-images');
        imagesContainer.style.opacity = '1';
        
        requestAnimationFrame(() => {
          this.tryInitializeSlick(imagesContainer, thumbnailsContainer, productData);
        });
      });
      } else {
        console.log('Only one image found, no slick slider needed');
        // Still need to ensure single image loads
        imagesContainer.classList.remove('loading-images');
        imagesContainer.style.opacity = '1';
      }
      
    }
    
    tryInitializeSlick(imagesContainer, thumbnailsContainer, productData) {
      // Ensure slick controls exist
      let slickControls = document.querySelector('.slick-external-controls');
      if (!slickControls) {
        // Create controls container if it doesn't exist
        slickControls = document.createElement('div');
        slickControls.className = 'slick-external-controls';
        slickControls.innerHTML = '<div class="slick-arrows"></div><div class="slick-dots"></div>';
        
        // Insert after images container
        imagesContainer.parentNode.insertBefore(slickControls, imagesContainer.nextSibling);
      }
      
      // Initialize slick slider if we have multiple images
      if ((productData.media && productData.media.length > 1) || (productData.images && productData.images.length > 1)) {
        // Check if jQuery and slick are available
        if (typeof $ === 'undefined' || !$.fn || !$.fn.slick) {
          console.warn('Slick slider not ready, waiting...');
          
          // Try to wait for slick to load
          let retryCount = 0;
          const checkAndInitialize = setInterval(() => {
            retryCount++;
            
            if (typeof $ !== 'undefined' && $.fn && $.fn.slick) {
              clearInterval(checkAndInitialize);
              this.initializeSlickSliders(imagesContainer, thumbnailsContainer);
            } else if (retryCount > 10) {
              console.error('Slick slider failed to load');
              clearInterval(checkAndInitialize);
            }
          }, 500);
          
          return;
        }
        
        // Initialize the slick sliders
        this.initializeSlickSliders(imagesContainer, thumbnailsContainer);
      }
      
      // Reinitialize any other image functionality (like zoom)
      if (window.initProductImages) {
        window.initProductImages();
      }
      
      // Initialize PhotoSwipe if available
      if (window.initPhotoSwipe) {
        window.initPhotoSwipe();
      }
      
      // Trigger load event on images to ensure they're rendered
      imagesContainer.querySelectorAll('img').forEach(img => {
        if (img.complete) {
          img.classList.add('loaded');
        } else {
          img.addEventListener('load', function() {
            this.classList.add('loaded');
          });
        }
      });
    }
    
    initializeSlickSliders(imagesContainer, thumbnailsContainer) {
      // Check if already initialized
      if ($(imagesContainer).hasClass('slick-initialized')) {
        return;
      }
      
      try {
        // Main slideshow
        const imageCount = imagesContainer.querySelectorAll('.product-detail__image').length;
        console.log(`Initializing slick slider with ${imageCount} images`);
        
        $(imagesContainer).slick({
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
        fade: true,
        asNavFor: '.product-detail__thumbnails',
        adaptiveHeight: true,
        dots: false,
        waitForAnimate: false,
        responsive: [{
          breakpoint: 767.98,
          settings: {
            fade: false,
            adaptiveHeight: false,
            dots: true
          }
        }]
      });
      
        // Thumbnail navigation
        if (thumbnailsContainer) {
          $(thumbnailsContainer).slick({
            slidesToShow: 5,
            slidesToScroll: 1,
            asNavFor: '.product-detail__images',
            dots: false,
            arrows: true,
            prevArrow: '<button type="button" class="slick-prev"><svg fill="#000000" width="24" height="24" viewBox="-128 0 512 512" xmlns="http://www.w3.org/2000/svg"><path d="M31.7 239l136-136c9.4-9.4 24.6-9.4 33.9 0l22.6 22.6c9.4 9.4 9.4 24.6 0 33.9L127.9 256l96.4 96.4c9.4 9.4 9.4 24.6 0 33.9L201.7 409c-9.4 9.4-24.6 9.4-33.9 0l-136-136c-9.5-9.4-9.5-24.6-.1-34z"></path></svg></button>',
            nextArrow: '<button type="button" class="slick-next"><svg fill="#000000" width="24" height="24" viewBox="-128 0 512 512" xmlns="http://www.w3.org/2000/svg"><path d="M224.3 273l-136 136c-9.4 9.4-24.6 9.4-33.9 0l-22.6-22.6c-9.4-9.4-9.4-24.6 0-33.9l96.4-96.4-96.4-96.4c-9.4-9.4-9.4-24.6 0-33.9L54.3 103c9.4-9.4 24.6-9.4 33.9 0l136 136c9.5 9.4 9.5 24.6.1 34z"></path></svg></button>',
            infinite: false,
            centerMode: false,
            focusOnSelect: true,
            responsive: [{
              breakpoint: 767.98,
              settings: {
                slidesToShow: 4,
                slidesToScroll: 1
              }
            }]
          });
        }
        
        // Handle thumbnail clicks
        document.querySelectorAll('[data-product-media-thumbnail]').forEach(thumb => {
          thumb.addEventListener('click', (e) => {
            e.preventDefault();
            const mediaId = thumb.dataset.mediaId;
            const targetSlide = document.querySelector(`.product-detail__images [data-media-id="${mediaId}"]`);
            if (targetSlide) {
              const slideIndex = $(targetSlide).closest('.slick-slide').data('slick-index');
              $(imagesContainer).slick('slickGoTo', slideIndex);
            }
          });
        });
        
        console.log('✓ Slick slider initialized successfully');
        
      } catch (error) {
        console.error('Error initializing slick:', error);
      }
    }

    updateActiveColor(activeOption) {
      // Remove active class from all options
      this.container.querySelectorAll('.color-variant-option').forEach(option => {
        option.classList.remove('active');
      });
      
      // Add active class to selected option
      activeOption.classList.add('active');
      
      // Update container data and instance variables
      this.currentColor = activeOption.dataset.color;
      this.container.dataset.currentColor = this.currentColor;
    }

    updateURL(handle) {
      // Update browser URL without page reload
      const newURL = `/products/${handle}`;
      history.pushState({ handle }, '', newURL);
    }

    formatPrice(cents) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(cents / 100);
    }

    setLoadingState(loading) {
      this.isLoading = loading;
      this.container.classList.toggle('color-variants-loading', loading);
    }

    showError(message) {
      // Show error message (you can customize this)
      console.error(message);
      // You could add a toast notification or alert here
    }
  }

  // Initialize when DOM is ready
  function initColorVariantSwitcher() {
    new ColorVariantSwitcher();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initColorVariantSwitcher);
  } else {
    initColorVariantSwitcher();
  }

  // Handle browser back/forward buttons
  window.addEventListener('popstate', function(event) {
    if (event.state && event.state.handle) {
      location.reload(); // Simple reload for now, could be made more sophisticated
    }
  });

})();
